/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { useParams } from 'react-router-dom';
import { Row, Col } from 'antd';

import { useArticle, useArticlesByIds } from '@api/hooks';
import { NO_MARGIN_BOTTOM } from '@constants';
import { Typography } from '@atoms';
import { ArticleField, ModeSelect, PositionField, TextField, ContentModuleModal } from '@molecules';
import MediaField from '@organisms/MediaField';
import { getImage } from '@utils/media';

import Form from '../Form';

import { INITIAL_VALUES, FORM_FIELD, FORM_FIELDS, MODE, MODES } from './constants';
import Item from './Item';

const LifestylePanel = ({
  visible,
  onSubmit,
  hide,
  previewImg,
  onDelete,
  initialValues: data = INITIAL_VALUES,
  errors,
}) => {
  const [form] = Form.useForm(FORM_FIELDS);
  const initialValues = React.useMemo(() => form.deserialize(data), [data, form]);
  const { locale } = useParams();

  const articleQuery = useArticle(data?.articleId);
  React.useEffect(() => {
    if (visible && articleQuery.status === 'success') {
      form.setFieldsValue({ [FORM_FIELD.ARTICLE.name]: articleQuery.data });
    }
  }, [articleQuery, articleQuery.article, articleQuery.data, articleQuery.status, form, visible]);

  const featuredArticlesQuery = useArticlesByIds(
    data?.links.map(({ articleId }) => articleId).filter(Boolean),
    locale,
    {
      throwError: false,
    },
  );
  React.useEffect(() => {
    if (visible && featuredArticlesQuery.status === 'success' && featuredArticlesQuery.data) {
      form.setFieldsValue({ [FORM_FIELD.LINKS.name]: featuredArticlesQuery.data.map((article) => ({ article })) });
    }
  }, [featuredArticlesQuery, featuredArticlesQuery.data, featuredArticlesQuery.status, form, visible]);

  const [mode, setMode] = React.useState();
  React.useEffect(() => {
    if (visible) {
      setMode(data?.articleId ? MODE.EXISTING.value : MODE.MANUAL.value);
    }
  }, [data, visible]);

  const handleClose = () => {
    form.resetFields();
    hide();
  };

  const handleSubmit = (values) => {
    onSubmit(values);
    handleClose();
  };

  const handleOnValuesChange = ({ [FORM_FIELD.ARTICLE.name]: article }) => {
    if (article && mode === MODE.EXISTING.value) {
      form.setFieldsValue({
        [FORM_FIELD.IMAGE.name]: getImage(article, 'featureImage'),
      });
    }
  };

  return (
    <ContentModuleModal
      visible={visible}
      title="Lifestyle panel"
      subtitle="Choose a lifestyle news story to feature on the page"
      okText={onDelete ? 'SAVE CHANGES' : 'ADD LIFESTYLE PANEL'}
      onCancel={handleClose}
      onSubmit={form.submit}
      onDelete={onDelete}
      heroImageSrc={previewImg}
      errors={errors}
    >
      <Form
        form={form}
        layout="vertical"
        wrapperCol={{ span: 24 }}
        initialValues={initialValues}
        onSerializedFinish={handleSubmit}
        onValuesChange={handleOnValuesChange}
      >
        <ModeSelect onSelect={setMode} mode={mode} modes={MODES} />
        {mode === MODE.MANUAL.value ? (
          <>
            <MediaField {...FORM_FIELD.IMAGE} />
            <TextField {...FORM_FIELD.TITLE} />
            <TextField {...FORM_FIELD.SUBTITLE} />
            <TextField {...FORM_FIELD.CATEGORY} />
            <TextField {...FORM_FIELD.STANDFIRST} />
            <TextField {...FORM_FIELD.URL} />
          </>
        ) : (
          <>
            <ArticleField {...FORM_FIELD.ARTICLE} isTypeFilter />
            <TextField {...FORM_FIELD.SUBTITLE} />
            <MediaField {...FORM_FIELD.IMAGE} />
          </>
        )}
        <Typography.Title level={3}>Feature links</Typography.Title>
        <Form.List {...FORM_FIELD.LINKS}>
          {(fields) => {
            return fields.map((record, idx) => (
              <React.Fragment key={idx}>
                <Typography.Title level={4}>Link {idx + 1}:</Typography.Title>
                {mode === MODE.MANUAL.value ? (
                  <>
                    <TextField {...FORM_FIELD.LINK_TITLE(idx)} />
                    <TextField {...FORM_FIELD.LINK_SUBTITLE(idx)} />
                    <TextField {...FORM_FIELD.LINK_URL(idx)} />
                  </>
                ) : (
                  <ArticleField {...FORM_FIELD.LINK_ARTICLE(idx)} />
                )}
              </React.Fragment>
            ));
          }}
        </Form.List>

        <Typography.Title level={3} style={{ marginTop: 40 }}>
          Settings
        </Typography.Title>
        <Row align="middle" gutter={20}>
          <Col flex="0 0 50%">
            <PositionField {...FORM_FIELD.TEXT_POSITION} style={NO_MARGIN_BOTTOM} />
          </Col>
        </Row>
      </Form>
    </ContentModuleModal>
  );
};

LifestylePanel.Item = Item;

export default LifestylePanel;
